import { ApolloClient, gql } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import compact from 'lodash/compact'
import {
  GetSweepstakeQuery,
  GetSweepstakeQueryVariables,
  GetSweepstakesQuery,
  GetSweepstakesQueryVariables,
  SweepstakeObject,
  SweepstakeStatus,
} from '@/types/codegen-federation'
import { logger } from '@/utils/logging'

export const GET_SWEEPSTAKE = gql`
  query getSweepstake($sweepstakeSlug: String!) {
    getSweepstake(input: { sweepstakeSlug: $sweepstakeSlug }) {
      sweepstake {
        id
        slug
        theatricalSlug
        sweepstakeSlug
        heroDesktopImageUrl
        heroMobileImageUrl
        startDate
        endDate
        winnerAnnouncedDate
        giveawayYoutubeId
        trailerYoutubeId
        rulesUrl
        projectName
        seoImageUrl
        entryVariantId
        bundlesCollectionId
        backgroundVideoUrl
        status
        title
        description
        squareImageUrl
        value {
          currencyAmount
          currencyCode
        }
      }
    }
  }
`

export const getSweepstake = async (client: ApolloClient, sweepstakeSlug: string): Promise<SweepstakeObject | null> => {
  try {
    const { data } = await client.query<GetSweepstakeQuery, GetSweepstakeQueryVariables>({
      query: GET_SWEEPSTAKE,
      variables: { sweepstakeSlug },
      errorPolicy: 'all',
    })

    return data?.getSweepstake?.sweepstake ?? null
  } catch (err) {
    logger().error('getSweepstake query failed', err)
    return null
  }
}

export const useSweepstake = (sweepstakeSlug: string) => {
  const { data, ...rest } = useQuery<GetSweepstakeQuery, GetSweepstakeQueryVariables>(GET_SWEEPSTAKE, {
    variables: { sweepstakeSlug },
    errorPolicy: 'all',
    skip: !sweepstakeSlug,
  })

  return {
    sweepstake: data?.getSweepstake?.sweepstake,
    isActive: data?.getSweepstake?.sweepstake?.status === 'LIVE',
    ...rest,
  }
}

export const GET_SWEEPSTAKES = gql`
  query getSweepstakes($input: GetSweepstakesInput!) {
    getSweepstakes(input: $input) {
      sweepstakes {
        id
        slug
        theatricalSlug
        sweepstakeSlug
        heroDesktopImageUrl
        heroMobileImageUrl
        startDate
        endDate
        winnerAnnouncedDate
        giveawayYoutubeId
        trailerYoutubeId
        rulesUrl
        projectName
        seoImageUrl
        entryVariantId
        bundlesCollectionId
        backgroundVideoUrl
        status
        title
        description
        squareImageUrl
        value {
          currencyAmount
          currencyCode
        }
      }
    }
  }
`

export const getSweepstakes = async (
  client: ApolloClient,
  statuses: SweepstakeStatus[],
): Promise<SweepstakeObject[]> => {
  try {
    const { data } = await client.query<GetSweepstakesQuery, GetSweepstakesQueryVariables>({
      query: GET_SWEEPSTAKES,
      variables: { input: { statuses } },
      errorPolicy: 'all',
    })

    return compact(data?.getSweepstakes?.sweepstakes) ?? []
  } catch (err) {
    logger().error('getSweepstake query failed', err)
    return []
  }
}
